import {
  ArrowLeftRight,
  CalendarX,
  FilePen,
  FileSignature,
  FileUp,
  RefreshCw,
} from "lucide-react";

import { formatDate, formatDateRange } from "@/lib/format";
import type { MyDocument } from "@/features/documents/api";
import { owedByEmployee } from "@/features/employees/documentTasks";
import { isRenewalTask } from "@/features/renewals/form";
import type { EmployeeTask } from "@/features/separation/api";
import type { Handover, LeaveRequest } from "@/features/leave/types";

import type { NavCounts } from "./hooks";
import type { InboxItem } from "./Inbox";

export interface WaitingOnMe {
  /** The figures the sidebar already shows beside its items. */
  counts: NavCounts | undefined;
  documents: MyDocument[] | undefined;
  requests: LeaveRequest[] | undefined;
  /** Work colleagues have left with this person while they are away. */
  handovers: Handover[] | undefined;
  /** Whether a break at the branch is theirs to answer. */
  isManager: boolean;
  /** Today as YYYY-MM-DD, so a date can be read as due, overdue or neither. */
  today: string;
  /** Their own checklist, for the papers they still have to upload. */
  checklist?: EmployeeTask[] | undefined;
  /** Corrections HR asked of them and they have not yet sent back. */
  corrections?: { count: number; note: string | null } | null;
}

/**
 * What is waiting on an employee, as rows for their inbox.
 *
 * Every row is something the page already knows: the sidebar's own counts,
 * and the documents, leave requests and handovers this person can already
 * see elsewhere. Nothing is listed that cannot be counted, and nothing is
 * listed at zero.
 *
 * Red is kept for a row with a date on it that has arrived: a document due
 * today or overdue, cover for a colleague who has already gone. Everything
 * else is ordinary work.
 */
export function waitingOnMe({
  counts,
  documents,
  requests,
  handovers,
  today,
  checklist,
  corrections,
}: WaitingOnMe): InboxItem[] {
  const items: InboxItem[] = [];

  // HR checked their record and asked for some of it to be put right. The
  // account stays open as usual, so this is a row, not a locked door.
  if (corrections) {
    items.push({
      key: "corrections",
      mark: <FilePen className="size-4" />,
      label: "Correct the details HR marked",
      detail:
        corrections.note ??
        (corrections.count === 1 ? "One detail to correct" : `${corrections.count} details to correct`),
      link: "/profile/corrections",
      urgency: "normal",
      verb: "Correct",
    });
  }

  // A paper everybody must have and they have not given: the police
  // clearance, or a UAE national's family book. Uploaded from their documents.
  for (const task of owedByEmployee(checklist)) {
    items.push({
      key: `task:${task.id}`,
      mark: <FileUp className="size-4" />,
      label: `Upload your ${task.title.toLowerCase()}`,
      detail: "HR asks everybody for it",
      link: "/documents",
      urgency: "normal",
      verb: "Upload",
    });
  }

  // A paper HR has marked as under renewal: the new one is theirs to bring.
  for (const task of checklist ?? []) {
    if (!isRenewalTask(task) || task.owner !== "employee" || task.status !== "pending") continue;
    items.push({
      key: `task:${task.id}`,
      mark: <RefreshCw className="size-4" />,
      label: task.title,
      detail: task.description ?? "The new issue date, expiry date and a copy",
      link: "/documents",
      urgency: "normal",
      verb: "Upload",
    });
  }

  // One row per document rather than a count, so the row can carry what the
  // card it replaced carried: the title, how many places, when it is due, and
  // a way straight to the page where it is signed.
  for (const document of documents ?? []) {
    if (document.my_status !== "pending") continue;
    const due = document.due_date ? document.due_date.slice(0, 10) : null;
    const places =
      document.my_field_count === 1
        ? "One place to sign"
        : `${document.my_field_count} places to sign`;
    items.push({
      key: `document:${document.document_id}`,
      mark: <FileSignature className="size-4" />,
      label: `Sign ${document.title}`,
      detail:
        due === null
          ? places
          : due < today
            ? `${places} · was due ${formatDate(due)}`
            : `${places} · due ${formatDate(due)}`,
      link: `/documents/${document.document_id}/sign`,
      urgency: due !== null && due <= today ? "urgent" : "normal",
      verb: "Sign",
    });
  }

  for (const handover of handovers ?? []) {
    if (handover.accepted_at || handover.handback_at || handover.closed_at) continue;
    const away =
      handover.start_date && handover.end_date
        ? `Away ${formatDateRange(handover.start_date, handover.end_date)}`
        : null;
    items.push({
      key: `handover:${handover.id}`,
      mark: <ArrowLeftRight className="size-4" />,
      label: `${handover.employee_name ?? "A colleague"} handed their work to you`,
      detail: away ? `${away} · waiting for you to accept` : "Waiting for you to accept",
      link: "/leaves",
      urgency: handover.start_date !== null && handover.start_date <= today ? "urgent" : "normal",
    });
  }

  // Breaks waiting at the branch are not listed. The head teller may answer
  // them, but the only screen that can is HR's; a row that leads to a page
  // with no way to act on it is worse than no row. It belongs here once the
  // employee's own Breaks screen can answer one.

  const reviews = counts?.kpi ?? 0;
  if (reviews > 0) {
    items.push({
      key: "kpi",
      count: reviews,
      label: reviews === 1 ? "KPI review to write" : "KPI reviews to write",
      link: "/kpi",
      urgency: "normal",
    });
  }

  const tasks = counts?.my_work ?? 0;
  if (tasks > 0) {
    items.push({
      key: "my_work",
      count: tasks,
      label: tasks === 1 ? "task assigned to you" : "tasks assigned to you",
      link: "/my-work",
      urgency: "normal",
    });
  }

  // A request that was turned down for dates still ahead: the person may
  // want to ask again for different ones. Once the dates have passed there is
  // nothing left to do about it, and it goes.
  for (const request of requests ?? []) {
    if (request.status !== "rejected" || request.end_date < today) continue;
    const dates = formatDateRange(request.start_date, request.end_date);
    const what = request.leave_type_name ? `${request.leave_type_name}, ${dates}` : dates;
    items.push({
      key: `leave-declined:${request.id}`,
      mark: <CalendarX className="size-4" />,
      label: "A leave request was declined",
      detail: request.decision_comment ? `${what} · ${request.decision_comment}` : what,
      link: "/leaves",
      urgency: "low",
    });
  }

  // What cannot wait first. The sort is stable, so within a kind the rows
  // stay in the order they were gathered.
  const rank = { urgent: 0, normal: 1, low: 2 } as const;
  return items.sort((a, b) => rank[a.urgency] - rank[b.urgency]);
}
